import { Badge, Box, HStack, Spinner, Table, TableContainer, Tbody, Td, Th, Tr, VStack } from '@chakra-ui/react';
import { Icon, IconMap } from '@common/components/Icon';
import { AssetClass } from '@services/appsync/types';
import React from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { NumericFormat } from 'react-number-format';

type RecordViewProps = {
  activityDesc: string;
  activityName: string;
  amount: string;
  assetClass?: AssetClass | null;
  data?: [label: string, value: string][];
  dataIsLoading?: boolean;
  dateLabel: string;
  fund?: string;
  icon?: keyof typeof IconMap | null;
  onExpand?: () => void;
  status: string;
};

export const RecordView: React.FC<RecordViewProps> = ({
  activityName,
  activityDesc,
  amount,
  assetClass,
  data = [],
  dataIsLoading = false,
  dateLabel,
  icon,
  onExpand,
  status,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <VStack
      w="full"
      flexDirection={'column'}
      borderWidth={'1px'}
      borderColor={'gray.100'}
      borderRadius={'md'}
      justifyContent="space-between"
      gap={'20px'}
      p={'20px'}>
      <HStack
        gap={'20px'}
        w="full"
        cursor="pointer"
        onClick={() => {
          if (!isOpen && onExpand) onExpand();
          setIsOpen(!isOpen);
        }}>
        <VStack flex="1" textAlign="left" align={'start'} spacing="0">
          <TextBlock fontSize={'12px'} color="gray.500">
            {dateLabel}
          </TextBlock>
          <HStack>
            <TextBlock>{activityName}</TextBlock>
            {activityDesc && (
              <Badge
                variant="solid"
                colorScheme={assetClass ? (assetClass === AssetClass.SFR ? 'blue' : 'orange') : 'gray'}>
                {activityDesc}
              </Badge>
            )}
          </HStack>
        </VStack>
        <VStack flex="1" textAlign="left" align={'end'} spacing="0">
          <TextBlock fontSize={'12px'} color="gray.500">
            {status}
          </TextBlock>
          <HStack>
            {icon && <Icon icon={icon} color="green.300" />}
            <TextBlock weight="bold"><NumericFormat prefix={'$'} fixedDecimalScale value={amount} thousandSeparator="," displayType="text" decimalScale={amount.toString().split('.')[1]?.length > 2 ? 8 : 2} /></TextBlock>
          </HStack>
        </VStack>
        <Icon icon={isOpen ? 'chevron-down' : 'chevron-right'} boxSize={'15px'} color="gray.200" />
      </HStack>
      <Box mt={5} mb={10} display={isOpen ? 'block' : 'none'} w="full">
        {dataIsLoading ? (
          <Box w="full" textAlign={'center'} p={10}>
            <Spinner color="blue.500" />
          </Box>
        ) : (
          <TableContainer w="full">
            <Table w="full">
              <Tbody>
                {data.map(([label, value], i) => {
                  return (
                    <Tr key={`data-${i}`}>
                      <Th whiteSpace={'break-spaces'} borderBottomStyle={i < data.length - 1 ? 'solid' : 'none'}>
                        {label}
                      </Th>
                      <Td textAlign={'right'} borderBottomStyle={i < data.length - 1 ? 'solid' : 'none'}>
                        {value}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </VStack>
  );
};
